import "./OutcomeInfo.scss";

import clsx from "clsx";
import { useEffect } from "react";

import { useFetchAssessmentBaseData } from "components/materials/section/assessment/assessment-data";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { AccordionTitle } from "mds/components/AccordionTitle";
import { storeApi, useAppSelector } from "store/index";
import {
  NestedSectionOutcomeType,
  selectChunkedPagesForOutcomeByTopic,
  selectPagesForOutcome,
} from "store/selectors";
import { Link } from "utils/routing";
import { pageUrl } from "utils/urls";

/**
 * The number of pages that must be present before the topics start being collapsed into an accordion.
 */
const MAX_PAGE_AMOUNT_BEFORE_ACCORDION = 6;

/**
 * Displays a list of all pages that are attached to the course outcome,
 * organized by category with simple titles.
 */
export const OutcomeUseIndex = ({ courseOutcome }: { courseOutcome: NestedSectionOutcomeType }) => {
  const pagesForOutcome = useAppSelector((s) => selectPagesForOutcome(s, courseOutcome.id));
  const chunkedPages = useAppSelector((s) =>
    selectChunkedPagesForOutcomeByTopic(s, courseOutcome.id),
  );

  useEffect(() => {
    if (courseOutcome?.id) {
      storeApi.page_outcomes.list({ section_outcome_id: courseOutcome.id });
    }
  }, [courseOutcome?.id]);

  useFetchAssessmentBaseData();

  const topicPages = pagesForOutcome.filter((p) => p.category === "activity_page");
  const assignmentPages = pagesForOutcome.filter((p) => p.category === "assignment");
  const resourcePages = pagesForOutcome.filter((p) => p.category === "course_resource");

  return (
    <div>
      <h2 className="h3 m-0 mb-3 text-black-tint-40">{t("outcomes.usage.title")}</h2>
      {!pagesForOutcome?.length && (
        <div className="body-m rounded p-4">
          {t("outcomes.usage.not_using_yet", { outcome_title: courseOutcome.outcome.title })}
        </div>
      )}
      {Boolean(pagesForOutcome?.length) && (
        <div className="space-y-4">
          {topicPages.length > 0 && (
            <div>
              <h3 className="h3 m-0 mb-2">
                {t("glossary.topics")} ({topicPages.length})
              </h3>
              <div className="mt-2">
                {chunkedPages
                  .filter((topicChunk) => topicChunk.pages.length > 0)
                  .map((topicChunk) => (
                    <AccordionTitle
                      className="text-black-tint-40"
                      key={topicChunk.id}
                      startOpen={topicPages.length < MAX_PAGE_AMOUNT_BEFORE_ACCORDION}
                      title={<span className="text-black-tint-10">{topicChunk.title}</span>}
                    >
                      {topicChunk.pages.map((p) => (
                        <OutcomeUseListPageItem
                          className="my-2"
                          href={pageUrl(courseOutcome.section_id, p.id)}
                          key={p.id}
                          page={p}
                        />
                      ))}
                    </AccordionTitle>
                  ))}
              </div>
            </div>
          )}
          {assignmentPages.length > 0 && (
            <>
              <h3 className="h3 m-0 mb-2 text-black-tint-10">
                {t("glossary.assignments")} ({assignmentPages.length})
              </h3>
              <div className="mt-2">
                {assignmentPages.map((p) => (
                  <OutcomeUseListPageItem
                    className="mb-2"
                    href={pageUrl(courseOutcome.section_id, p.id)}
                    key={p.id}
                    page={p}
                  />
                ))}
              </div>
            </>
          )}
          {resourcePages.length > 0 && (
            <div>
              <h3 className="h3 m-0 mb-2 text-black-tint-10">
                {t("glossary.course_resources")} ({resourcePages.length})
              </h3>
              <div className="mt-2">
                {resourcePages.map((p) => (
                  <OutcomeUseListPageItem
                    className="mb-2"
                    href={pageUrl(courseOutcome.section_id, p.id)}
                    key={p.id}
                    page={p}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const OutcomeUseListPageItem = ({
  page,
  href,
  className,
}: {
  page: PageType;
  href: string;
  className?: string;
}) => {
  return (
    <div className={clsx("body-m flex items-center justify-between", className)}>
      <Link to={href}>{page.title}</Link>
      {page.max_points > 0 && (
        <div className="text-black-tint-40">
          {t("rubric_card.assessment_points_other", { count: page.max_points })}
        </div>
      )}
    </div>
  );
};
